import React, { useState } from 'react';
import { APP_VERSION } from '../version';
import './AppUpdate.css';

type Status = 'idle' | 'checking' | 'current' | 'available' | 'error';

// The hashed main bundle actually running in this tab, e.g.
// "/static/js/main.4d2f8a.js". CRA injects this <script> into index.html at
// build time, so its hash changes on every deploy — the basis for detecting a
// newer build below. Returns null on the dev server (unhashed bundle.js).
function loadedBundle(): string | null {
  const re = /\/static\/js\/main\.[^/]+\.js$/;
  const el = Array.from(document.getElementsByTagName('script')).find((s) => re.test(s.src));
  return el ? new URL(el.src).pathname.split('/').pop() ?? null : null;
}

// App Update — for the local-first web build "updating" means loading the
// freshly deployed static bundle. This checks the deployed asset-manifest.json
// (CRA emits one per build) against the bundle currently running, then offers a
// cache-busting reload to pick up a newer build. No APK / version endpoint.
const AppUpdate: React.FC = () => {
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState('');

  const check = async () => {
    setStatus('checking');
    setError('');
    try {
      const base = process.env.PUBLIC_URL || '';
      const res = await fetch(`${base}/asset-manifest.json`, { cache: 'no-store' });
      if (!res.ok) throw new Error(`manifest ${res.status}`);
      const manifest = await res.json();
      const deployed: string | undefined = manifest?.files?.['main.js'];
      const running = loadedBundle();
      // The dev server has no hashed manifest/bundle; nothing to compare, so
      // treat the running build as current rather than reporting a false update.
      if (!deployed || !running) {
        setStatus('current');
        return;
      }
      setStatus(deployed.split('/').pop() === running ? 'current' : 'available');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setStatus('error');
    }
  };

  const reload = async () => {
    // Best-effort: drop any cached build (service worker / Cache Storage) so the
    // browser fetches the freshly deployed files instead of a stale copy.
    try {
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
    } catch {
      /* reload anyway */
    }
    window.location.reload();
  };

  return (
    <div className="upd-page">
      <div className="page-header">
        <h1>App Update</h1>
      </div>

      <div className="upd-card">
        <div className="upd-icon">⬆️</div>

        <div className="upd-version">
          <span className="upd-version-label">Current version</span>
          <span className="upd-version-value">{APP_VERSION}</span>
        </div>

        <p className="upd-note">
          The POS loads the latest deployed build each time it starts — there is no
          APK to download. Check whether a newer build is available and reload to
          apply it.
        </p>

        {status === 'available' && (
          <div className="upd-status upd-status-new">
            A newer build is available. Reload to update.
          </div>
        )}
        {status === 'current' && (
          <div className="upd-status upd-status-ok">You’re on the latest build.</div>
        )}
        {status === 'error' && (
          <div className="upd-status upd-status-err">
            Couldn’t check for updates ({error}). You can still reload manually.
          </div>
        )}

        <div className="upd-actions">
          <button className="upd-btn upd-btn-ghost" onClick={check} disabled={status === 'checking'}>
            {status === 'checking' ? 'Checking…' : 'Check for updates'}
          </button>
          <button
            className={`upd-btn ${status === 'available' ? 'upd-btn-primary' : 'upd-btn-ghost'}`}
            onClick={reload}
          >
            Reload now
          </button>
        </div>
      </div>
    </div>
  );
};

export default AppUpdate;
